/**
 * UserLocaleEditDialog.tsx — root 手动修正用户地区 / 语言（测试与特殊情况）
 *
 * 一次改一项，走 PATCH /api/v1/users/:id/locale（usersApi.updateLocale），修改原因必填、随操作日志落库。
 * 值留空 = 清空该项；国家按两位大写国家码、语言按 xx / xx-XX 校验（与后端 Zod 同口径）。
 * 成功后刷新详情与列表；失败 toast 不关弹窗。
 */

import type { UserLocaleEditableField } from '@novel-admin/shared';
import { onlineManager, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { usersApi } from '~/hooks/use-users';
import { fmtCountry, fmtLang } from '~/lib/format';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { toast } from '~/ui/Toast';

export type UserLocaleEditTarget = {
  field: UserLocaleEditableField;
  label: string;
  kind: 'country' | 'language';
  /** 当前值（国家码 / 语言码），无值传 null */
  current: string | null;
  /** 修改后的影响说明，展示在输入框下方 */
  note: string;
};

export function UserLocaleEditDialog({
  userId,
  target,
  onClose,
}: {
  userId: string;
  target: UserLocaleEditTarget;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isCountry = target.kind === 'country';
  const [value, setValue] = useState(target.current ?? '');
  const [reason, setReason] = useState('');
  const normalized = isCountry ? value.trim().toUpperCase() : value.trim();
  const pattern = isCountry ? /^[A-Z]{2}$/ : /^[a-z]{2}(-[A-Z]{2})?$/;
  const valueError =
    normalized && !pattern.test(normalized)
      ? isCountry
        ? '须为两位大写国家码，如 NO'
        : '须为 xx 或 xx-XX，如 pl / pt-BR'
      : null;
  const unchanged = normalized === (target.current ?? '');
  const mutation = useMutation({
    networkMode: 'always',
    retry: false,
    mutationFn: () => {
      if (!onlineManager.isOnline()) throw new Error('当前离线，请联网后重新确认操作');
      return usersApi.updateLocale(userId, {
        field: target.field,
        value: normalized || null,
        reason: reason.trim(),
      });
    },
    onSuccess: () => {
      toast(`已修改${target.label}`, { tone: 'pos', icon: 'IconCheck' });
      queryClient.invalidateQueries({ queryKey: ['user-detail', userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '修改失败，请重试', {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });
  const format = isCountry ? fmtCountry : fmtLang;
  const canSubmit = !mutation.isPending && !valueError && !unchanged && reason.trim().length > 0;
  return (
    <Dialog
      width={520}
      title={`修改${target.label}`}
      subtitle="仅超管可操作 · 修改与原因会记入操作日志"
      icon="IconEdit"
      onClose={() => {
        if (!mutation.isPending) onClose();
      }}
      footer={
        <>
          <button
            className="btn"
            onClick={onClose}
            disabled={mutation.isPending}
            style={{ marginLeft: 'auto' }}
          >
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!canSubmit}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? '保存中…' : '保存修改'}
          </button>
        </>
      }
    >
      <DialogBody>
        <FormRow label="当前值" labelWidth={100} pad="8px 0">
          <span style={{ fontSize: 12.5 }}>{format(target.current) || '—'}</span>
        </FormRow>
        <FormRow
          label="新值"
          labelWidth={100}
          pad="8px 0"
          required
          hint={isCountry ? '如 NO · 留空清空' : '如 pl · pt-BR · 留空清空'}
        >
          <div className="vstack" style={{ gap: 4 }}>
            <input
              className="input mono"
              value={value}
              autoFocus
              placeholder={isCountry ? 'NO' : 'pl'}
              onChange={(e) => setValue(e.target.value)}
              aria-label={`${target.label}新值`}
            />
            {valueError ? (
              <span style={{ fontSize: 11, color: 'var(--neg)' }}>{valueError}</span>
            ) : (
              normalized && (
                <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{format(normalized)}</span>
              )
            )}
          </div>
        </FormRow>
        <FormRow label="修改原因" labelWidth={100} pad="8px 0" required labelPadTop={6}>
          <input
            className="input"
            value={reason}
            maxLength={500}
            placeholder="如：联调测试 / 客服核实后更正"
            onChange={(e) => setReason(e.target.value)}
            aria-label="修改原因"
          />
        </FormRow>
        <div
          className="card-flat"
          style={{
            padding: '8px 10px',
            fontSize: 11.5,
            color: 'var(--fg-muted)',
            lineHeight: 1.55,
          }}
        >
          {target.note}
        </div>
      </DialogBody>
    </Dialog>
  );
}
